import React from "react";
import { Select, Radio, Col, Tabs, Row } from "antd";
import { resetRevstackDashboardWidgetData, updateRevstackLayoutFilters } from "@features/revstackWidgetsSlice";
import { useDispatch, useSelector } from "react-redux";
import { BsChevronRight } from "react-icons/bs";
import { ComingSoonTooltip } from "@/components/widgets";
import { normalizeRevstackLayoutFilters } from "@/ts/revstack/helpers";
import { applyCurrentFiltersWithHash } from "@/ts/revstack/filters";
import { CA_DD_MODULES, IRevstackLayoutFilters, RevstackDashboardSubModules } from "@ts/common-types";

interface IRevstackTabsSection {
    items: any;
    activeTab: RevstackDashboardSubModules | CA_DD_MODULES;
    setActiveTab: (tab: RevstackDashboardSubModules | CA_DD_MODULES) => void;
    type?: "default" | "drilldown" | "default-table";
    onTrendsViewClick?: () => void;
    onAdvancedChartsClick?: () => void;
    isTrendsViewRequired?: boolean;
    isAdvancedChartsRequired?: boolean;
}

const RevstackTabsSection: React.FC<IRevstackTabsSection> = ({
    items,
    activeTab,
    setActiveTab,
    type = "default",
    onTrendsViewClick,
    onAdvancedChartsClick,
    isAdvancedChartsRequired = false,
    isTrendsViewRequired = false
}): JSX.Element => {
    const dispatch = useDispatch();
    const revstackLayoutFilters = useSelector(
        (state: { revstackLayoutFilters: { value: IRevstackLayoutFilters } }) => state.revstackLayoutFilters.value
    );

    const handleTabChange = (key: string) => {
        setActiveTab(key as RevstackDashboardSubModules);
    };

    //TODO: as more module will be added it has to be made dynamic wrt modules
    const handleFilterChange = async (
        field: "priceType" | "metricType",
        value: any
    ) => {
        dispatch(resetRevstackDashboardWidgetData());
        const partiallyUpdatedFilters = {
            ...revstackLayoutFilters,
            [field]: {
                ...revstackLayoutFilters[field],
                selected: value,
            }
        };

        // Apply normalization logic based on metricType
        const updatedFilters = normalizeRevstackLayoutFilters(partiallyUpdatedFilters);

        dispatch(updateRevstackLayoutFilters(updatedFilters));

        await applyCurrentFiltersWithHash();
    };

    return (
        <div className="w-100 position-relative">
            <Tabs
                activeKey={activeTab || RevstackDashboardSubModules.OVERVIEW}
                items={items}
                className={`revstack-layout-tabs ${type === "drilldown" || type === "default-table" ? "drilldown-tabs" : ""} ${activeTab === "ai-insights" ? "ai-insights-active" : ""}`}
                moreIcon={null}
                onChange={handleTabChange}
                renderTabBar={(props, DefaultTabBar) =>
                    type === "default" ? (
                    <Row
                        className="w-100"
                        align="middle"
                        style={{
                            borderBottom: "1px solid #dee2e6",
                            margin: 0,
                            padding: 0,
                        }}
                    >
                        <Col flex="auto">
                            <DefaultTabBar {...props} />
                        </Col>
                        {props.activeKey !== "ai-insights" && (
                            <Col
                                className="d-flex align-items-center justify-content-end gap-1"
                                style={{ width: "321px" }}
                            >
                                <Radio.Group
                                    value={
                                        revstackLayoutFilters.priceType?.selected
                                    }
                                    buttonStyle="solid"
                                    onChange={(e) =>
                                        handleFilterChange("priceType", e.target.value)
                                    }
                                    className={revstackLayoutFilters.metricType.selected === "by_volume" ? "d-none" : "revstack-filter-radio-group"}
                                >
                                    {revstackLayoutFilters.metricType.options
                                        .find((option: any) => 
                                            option.value === revstackLayoutFilters
                                            .metricType.selected)
                                            ?.options
                                        .map((option: any) => (
                                            <Radio.Button
                                                key={option.value}
                                                className="w-50"
                                                value={option.value}
                                            >
                                                {option.label}
                                            </Radio.Button>
                                        ))}
                                </Radio.Group>
                                <Select
                                    defaultValue={
                                        revstackLayoutFilters.metricType.selected
                                    }
                                    className="revstack-filter-select mx-1"
                                    onChange={(value) =>
                                        handleFilterChange("metricType", value)
                                    }
                                    options={
                                        revstackLayoutFilters
                                        .metricType
                                        .options.map((option: any) => ({
                                            label: option.label,
                                            value: option.value
                                        }))
                                    }
                                />
                            </Col>
                        )}
                    </Row>
                )
                : type === "drilldown" ?
                (
                    <Row
                        id="revstack-drilldown-tabs"
                        className="w-100 bg-white d-flex align-items-center justify-content-between"
                    >
                        <Col flex="auto" style={{ minWidth: 0 }}>
                            <DefaultTabBar {...props} />
                        </Col>
                        {isTrendsViewRequired && (
                            <Col>
                                <span
                                    className="d-flex align-items-center gap-1 mb-0 text-primary"
                                    role="button"
                                    onClick={onTrendsViewClick}
                                    style={{ fontWeight: 600, fontSize: "14px" }}
                                >
                                    Trends View
                                    <BsChevronRight className="text-primary" />
                                </span>
                            </Col>
                        )}
                        {isAdvancedChartsRequired && (
                            <Col>
                                <ComingSoonTooltip>
                                    <span
                                        className="d-flex align-items-center gap-1 mb-0 text-primary"
                                        role="button"
                                        onClick={onAdvancedChartsClick}
                                        style={{ fontWeight: 600, fontSize: "14px" }}
                                    >
                                        Advanced Charts
                                        <BsChevronRight className="text-primary" />
                                    </span>
                                </ComingSoonTooltip>
                            </Col>
                        )}
                    </Row>
                ) : (
                    <Row
                        className="w-100 bg-white d-flex align-items-center justify-content-between border-bottom"
                        style={{
                            padding:"16px 20px 0px 20px"
                        }}
                    >
                        <Col flex="auto" style={{ minWidth: 0 }}>
                            <DefaultTabBar {...props} />
                        </Col>
                    </Row>
                )}
            />
        </div>
    );
};

export default RevstackTabsSection;